import React, { useContext, useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { AuthContext } from '../App';
import axios from 'axios';
import { toast } from 'sonner';
import CabinCrewLayout from '../layouts/CabinCrewLayout';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Progress } from '../components/ui/progress';
import { 
  ChevronLeft, Users, Mic, MicOff, Play, Volume2,
  Trophy, Star, ChevronRight, Sparkles, CheckCircle,
  Clock, MessageSquare, Loader2
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Interview question categories - EXPANDED CONTENT (60+ questions)
const INTERVIEW_CATEGORIES = [
  {
    id: 'personal',
    name: 'Kişisel Sorular',
    description: 'Kendinizi tanıtın, motivasyonunuz...',
    icon: '👤',
    color: 'from-blue-500 to-indigo-600',
    questions: [
      {
        question: "Tell me about yourself.",
        question_tr: "Kendinizden bahseder misiniz?",
        tips: ["Keep it professional", "Mention relevant experience", "Show enthusiasm for aviation"],
        sample_answer: "I am a dedicated professional with a passion for customer service and aviation. I have experience in hospitality and I'm excited about the opportunity to work as cabin crew.",
        sample_answer_tr: "Müşteri hizmetleri ve havacılığa tutkulu, özveri sahibi bir profesyonelim. Konaklama sektöründe deneyimim var ve kabin memuru olarak çalışma fırsatı beni heyecanlandırıyor."
      },
      {
        question: "Why do you want to be a cabin crew member?",
        question_tr: "Neden kabin memuru olmak istiyorsunuz?",
        tips: ["Show genuine passion", "Mention travel and meeting people", "Highlight service mindset"],
        sample_answer: "I've always been fascinated by aviation and love helping people. Being cabin crew combines my passion for travel with my dedication to customer service.",
        sample_answer_tr: "Havacılık her zaman ilgimi çekmiştir ve insanlara yardım etmeyi seviyorum. Kabin memurluğu, seyahat tutkumu müşteri hizmetlerine olan bağlılığımla birleştiriyor."
      },
      {
        question: "What are your strengths and weaknesses?",
        question_tr: "Güçlü ve zayıf yönleriniz nelerdir?",
        tips: ["Be honest but strategic", "Turn weakness into growth opportunity", "Give specific examples"],
        sample_answer: "My strength is my ability to stay calm under pressure. My weakness is that I can be too detail-oriented, but I'm learning to balance perfectionism with efficiency.",
        sample_answer_tr: "Güçlü yönüm baskı altında sakin kalabilmemdir. Zayıf yönüm çok detaycı olmam, ancak mükemmeliyetçiliği verimlilikle dengelemeyi öğreniyorum."
      },
      {
        question: "Where do you see yourself in five years?",
        question_tr: "Beş yıl içinde kendinizi nerede görüyorsunuz?",
        tips: ["Show ambition within the company", "Be realistic", "Show commitment"],
        sample_answer: "In five years, I see myself as a senior cabin crew member, possibly pursuing a purser position. I want to grow with this airline and contribute to its success.",
        sample_answer_tr: "Beş yıl içinde kendimi kıdemli bir kabin memuru, belki de kabin şefi pozisyonunu hedefliyor olarak görüyorum. Bu havayoluyla büyümek ve başarısına katkıda bulunmak istiyorum."
      },
      {
        question: "Why should we hire you?",
        question_tr: "Sizi neden işe almalıyız?",
        tips: ["Highlight unique qualities", "Match skills to job requirements", "Show confidence"],
        sample_answer: "I bring a unique combination of hospitality experience, language skills, and genuine passion for customer service. I'm adaptable, team-oriented, and committed to representing your brand with excellence.",
        sample_answer_tr: "Konaklama deneyimi, dil becerileri ve müşteri hizmetlerine gerçek tutkunun eşsiz bir kombinasyonunu getiriyorum. Uyum sağlayabilir, takım odaklı ve markanızı mükemmellikle temsil etmeye kararlıyım."
      },
      {
        question: "What do you know about our airline?",
        question_tr: "Havayolumuz hakkında ne biliyorsunuz?",
        tips: ["Do your research beforehand", "Mention specific achievements", "Show genuine interest"],
        sample_answer: "Your airline is known for its excellent customer service and has won numerous awards. You fly to over 300 destinations and are recognized for your commitment to safety and innovation.",
        sample_answer_tr: "Havayolunuz mükemmel müşteri hizmetleriyle tanınır ve sayısız ödül kazanmıştır. 300'den fazla noktaya uçuyorsunuz ve güvenlik ile yeniliğe olan bağlılığınızla tanınıyorsunuz."
      },
      {
        question: "How do you handle criticism?",
        question_tr: "Eleştiriyi nasıl karşılıyorsunuz?",
        tips: ["Show openness to feedback", "Give example of learning from criticism", "Stay positive"],
        sample_answer: "I view criticism as an opportunity to improve. When I receive feedback, I listen carefully, reflect on it, and take concrete steps to address any areas that need improvement.",
        sample_answer_tr: "Eleştiriyi gelişme fırsatı olarak görüyorum. Geri bildirim aldığımda dikkatle dinler, üzerinde düşünür ve iyileştirilmesi gereken alanlarda somut adımlar atarım."
      },
      {
        question: "What makes you unique compared to other candidates?",
        question_tr: "Sizi diğer adaylardan farklı kılan ne?",
        tips: ["Highlight special skills or experiences", "Be confident but not arrogant", "Be specific"],
        sample_answer: "My experience in emergency first aid, combined with my fluency in three languages and background in luxury hospitality, makes me uniquely qualified to provide exceptional service in any situation.",
        sample_answer_tr: "Üç dildeki akıcılığım ve lüks konaklamadaki geçmişimle birleşen acil ilk yardım deneyimim, beni herhangi bir durumda olağanüstü hizmet sunmak için benzersiz şekilde nitelikli kılıyor."
      }
    ]
  },
  {
    id: 'scenario',
    name: 'Senaryo Soruları',
    description: 'Zor yolcu, acil durumlar...',
    icon: '🎭',
    color: 'from-purple-500 to-pink-600',
    questions: [
      {
        question: "How would you handle a passenger who is afraid of flying?",
        question_tr: "Uçmaktan korkan bir yolcuyla nasıl başa çıkarsınız?",
        tips: ["Show empathy", "Explain safety procedures", "Offer practical help"],
        sample_answer: "I would approach them calmly, acknowledge their fear, explain the safety features, and offer to check on them throughout the flight. Distraction and reassurance are key.",
        sample_answer_tr: "Sakin bir şekilde yaklaşır, korkularını kabul eder, güvenlik özelliklerini açıklar ve uçuş boyunca kontrol edeceğimi söylerim. Dikkat dağıtma ve güven verme önemlidir."
      },
      {
        question: "What would you do if a passenger refuses to fasten their seatbelt?",
        question_tr: "Bir yolcu emniyet kemerini takmayı reddederse ne yaparsınız?",
        tips: ["Stay professional", "Explain regulations", "Escalate if necessary"],
        sample_answer: "I would politely explain the safety regulations and legal requirements. If they still refuse, I would inform the senior crew member and follow company procedures.",
        sample_answer_tr: "Güvenlik düzenlemelerini ve yasal gereklilikleri kibarca açıklarım. Hala reddederlerse, kıdemli ekip üyesini bilgilendirir ve şirket prosedürlerini takip ederim."
      },
      {
        question: "How would you handle a conflict between two passengers?",
        question_tr: "İki yolcu arasındaki bir çatışmayı nasıl çözersiniz?",
        tips: ["Stay neutral", "Separate if needed", "Find a solution"],
        sample_answer: "I would calmly approach both passengers, listen to each side separately, and try to find a compromise. If necessary, I would offer to reseat one of them.",
        sample_answer_tr: "Her iki yolcuya da sakin bir şekilde yaklaşır, her iki tarafı da ayrı ayrı dinler ve bir uzlaşma bulmaya çalışırım. Gerekirse, birini yeniden oturtmayı teklif ederim."
      },
      {
        question: "What would you do if you suspected a passenger was intoxicated?",
        question_tr: "Bir yolcunun sarhoş olduğundan şüphelenirseniz ne yaparsınız?",
        tips: ["Assess the situation", "Don't serve more alcohol", "Involve senior crew"],
        sample_answer: "I would discreetly assess their condition, stop serving alcohol, offer water and food instead. If their behavior becomes disruptive, I would inform the senior crew member immediately.",
        sample_answer_tr: "Durumlarını dikkatli bir şekilde değerlendirir, alkol servisini durdurur, bunun yerine su ve yiyecek teklif ederim. Davranışları rahatsız edici hale gelirse, hemen kıdemli ekip üyesini bilgilendiririm."
      },
      {
        question: "How would you handle a medical emergency on board?",
        question_tr: "Uçakta bir tıbbi acil durumla nasıl başa çıkarsınız?",
        tips: ["Stay calm", "Follow training", "Call for help"],
        sample_answer: "I would stay calm, assess the situation, and call for any medical professionals on board. I would follow my first aid training, get the medical kit, and keep the captain informed.",
        sample_answer_tr: "Sakin kalır, durumu değerlendirir ve uçaktaki tıp profesyonellerini çağırırım. İlk yardım eğitimimi takip eder, tıbbi kiti alır ve kaptanı bilgilendiririm."
      },
      {
        question: "A passenger complains the food is cold. What do you do?",
        question_tr: "Bir yolcu yemeğin soğuk olduğundan şikayet ediyor. Ne yaparsınız?",
        tips: ["Apologize sincerely", "Offer solution", "Follow up"],
        sample_answer: "I would apologize sincerely, offer to heat their meal or provide an alternative option. I'd follow up to ensure they're satisfied with the solution.",
        sample_answer_tr: "İçtenlikle özür diler, yemeklerini ısıtmayı veya alternatif bir seçenek sunmayı teklif ederim. Çözümden memnun olduklarından emin olmak için takip ederim."
      },
      {
        question: "What would you do if a colleague wasn't doing their job properly?",
        question_tr: "Bir meslektaşınız işini düzgün yapmıyorsa ne yaparsınız?",
        tips: ["Address privately", "Be constructive", "Involve supervisor if needed"],
        sample_answer: "I would first speak to them privately and offer help. If the issue persists and affects service or safety, I would bring it to the attention of the supervisor discreetly.",
        sample_answer_tr: "Önce onlarla özel olarak konuşur ve yardım teklif ederim. Sorun devam ederse ve hizmeti veya güvenliği etkilerse, durumu dikkatli bir şekilde amirime bildiririm."
      },
      {
        question: "How would you deal with a passenger who misses their connection due to a delay?",
        question_tr: "Gecikme nedeniyle aktarmasını kaçıran bir yolcuyla nasıl ilgilenirsiniz?",
        tips: ["Show empathy", "Provide information", "Coordinate with ground staff"],
        sample_answer: "I would apologize for the inconvenience, provide them with all available information about rebooking options, and coordinate with ground staff to ensure they receive assistance upon landing.",
        sample_answer_tr: "Rahatsızlık için özür diler, yeniden rezervasyon seçenekleri hakkında tüm mevcut bilgileri sağlar ve inişte yardım almalarını sağlamak için yer ekibiyle koordine olurum."
      }
    ]
  },
  {
    id: 'competency',
    name: 'Yetkinlik Soruları',
    description: 'Takım çalışması, problem çözme...',
    icon: '💼',
    color: 'from-green-500 to-teal-600',
    questions: [
      {
        question: "Give an example of when you worked effectively in a team.",
        question_tr: "Bir ekipte etkili çalıştığınız bir örnek verin.",
        tips: ["Use STAR method", "Be specific", "Highlight your contribution"],
        sample_answer: "In my previous job, our team had to handle a large event. I coordinated with colleagues, delegated tasks, and we successfully served 200 guests without any issues.",
        sample_answer_tr: "Önceki işimde, ekibimiz büyük bir etkinliği yönetmek zorundaydı. Meslektaşlarımla koordine oldum, görevleri dağıttım ve 200 misafire sorunsuz hizmet verdik."
      },
      {
        question: "Describe a time when you had to deal with a difficult customer.",
        question_tr: "Zor bir müşteriyle uğraşmak zorunda kaldığınız bir zamanı anlatın.",
        tips: ["Show patience", "Explain resolution", "Positive outcome"],
        sample_answer: "A customer was upset about a delayed order. I listened to their concerns, apologized sincerely, offered a solution, and they left satisfied with a complimentary service.",
        sample_answer_tr: "Bir müşteri gecikmeli sipariş nedeniyle üzgündü. Endişelerini dinledim, içtenlikle özür diledim, bir çözüm sundum ve ücretsiz hizmetle memnun ayrıldılar."
      },
      {
        question: "How do you handle stress and pressure?",
        question_tr: "Stres ve baskıyı nasıl yönetirsiniz?",
        tips: ["Be honest", "Give practical examples", "Show resilience"],
        sample_answer: "I stay organized and prioritize tasks. During busy periods, I take deep breaths and focus on one task at a time. Regular exercise also helps me manage stress.",
        sample_answer_tr: "Organize kalır ve görevleri öncelik sırasına koyarım. Yoğun dönemlerde derin nefes alır ve tek seferde bir göreve odaklanırım. Düzenli egzersiz de stresle başa çıkmama yardımcı olur."
      },
      {
        question: "Tell me about a time you went above and beyond for a customer.",
        question_tr: "Bir müşteri için beklenenin ötesine geçtiğiniz bir zamanı anlatın.",
        tips: ["Show initiative", "Specific example", "Impact of actions"],
        sample_answer: "A guest at my hotel was celebrating their anniversary but their cake order was lost. I quickly contacted a local bakery, arranged for a replacement, and had it delivered to their room with a personal note from the hotel.",
        sample_answer_tr: "Otelimde bir misafir yıldönümünü kutluyordu ama pasta siparişi kaybolmuştu. Hızlıca yerel bir pastanenin bağlantısını kurdum, yedek bir pasta ayarladım ve otelden kişisel bir notla odalarına teslim ettirdim."
      },
      {
        question: "Describe a situation where you had to adapt to change quickly.",
        question_tr: "Hızlı bir şekilde değişime uyum sağlamak zorunda kaldığınız bir durumu anlatın.",
        tips: ["Show flexibility", "Problem-solving skills", "Positive attitude"],
        sample_answer: "When our computer system crashed during peak hours, I quickly switched to manual processes, coordinated with my team, and ensured all customers were served efficiently despite the disruption.",
        sample_answer_tr: "Yoğun saatlerde bilgisayar sistemimiz çöktüğünde, hızla manuel süreçlere geçtim, ekibimle koordine oldum ve aksaklığa rağmen tüm müşterilere verimli bir şekilde hizmet verilmesini sağladım."
      },
      {
        question: "How do you prioritize tasks when you have multiple urgent demands?",
        question_tr: "Birden fazla acil talebiniz olduğunda görevleri nasıl önceliklendirirsiniz?",
        tips: ["Show organization skills", "Safety first mindset", "Communication"],
        sample_answer: "I assess which tasks are most critical, especially those related to safety. I communicate with my team to delegate when possible and tackle tasks systematically while keeping everyone informed.",
        sample_answer_tr: "Hangi görevlerin en kritik olduğunu, özellikle güvenlikle ilgili olanları değerlendiririm. Mümkün olduğunda delege etmek için ekibimle iletişim kurar ve herkesi bilgilendirirken görevleri sistematik olarak ele alırım."
      },
      {
        question: "Give an example of when you showed leadership.",
        question_tr: "Liderlik gösterdiğiniz bir örnek verin.",
        tips: ["Initiative and responsibility", "Outcome-focused", "Team impact"],
        sample_answer: "When our team leader was sick, I stepped up to coordinate a busy breakfast service. I assigned stations, maintained quality control, and ensured every guest received timely service.",
        sample_answer_tr: "Ekip liderimiz hastalandığında, yoğun bir kahvaltı servisini koordine etmek için öne çıktım. İstasyonları atadım, kalite kontrolünü sürdürdüm ve her misafirin zamanında hizmet almasını sağladım."
      },
      {
        question: "How do you maintain a positive attitude during long shifts?",
        question_tr: "Uzun vardiyalarda pozitif tutumu nasıl korursunuz?",
        tips: ["Self-care strategies", "Team support", "Focus on purpose"],
        sample_answer: "I focus on the bigger picture - the passengers I'm helping. I stay hydrated, take short breaks when possible, and maintain a supportive relationship with my colleagues.",
        sample_answer_tr: "Büyük resme odaklanırım - yardım ettiğim yolculara. Bol su içer, mümkün olduğunda kısa molalar verir ve meslektaşlarımla destekleyici bir ilişki sürdürürüm."
      }
    ]
  },
  {
    id: 'airline_specific',
    name: 'Havayolu Özel Soruları',
    description: 'Emirates, Qatar, THY...',
    icon: '✈️',
    color: 'from-amber-500 to-orange-600',
    questions: [
      {
        question: "Why do you want to work for our airline specifically?",
        question_tr: "Neden özellikle bizim havayolumuzda çalışmak istiyorsunuz?",
        tips: ["Research the airline", "Be specific about their values", "Show genuine interest"],
        sample_answer: "I admire your commitment to excellence and innovation. Your award-winning service standards align with my personal values, and I'm excited about the opportunity to represent such a prestigious brand.",
        sample_answer_tr: "Mükemmellik ve yeniliğe olan bağlılığınıza hayranım. Ödüllü hizmet standartlarınız kişisel değerlerimle uyuşuyor ve böylesine prestijli bir markayı temsil etme fırsatı beni heyecanlandırıyor."
      },
      {
        question: "Are you willing to relocate?",
        question_tr: "Taşınmaya istekli misiniz?",
        tips: ["Show flexibility", "Research base locations", "Express enthusiasm"],
        sample_answer: "Absolutely. I'm excited about the prospect of living in a new city and experiencing different cultures. I'm adaptable and see this as one of the most exciting aspects of the role.",
        sample_answer_tr: "Kesinlikle. Yeni bir şehirde yaşama ve farklı kültürleri deneyimleme olasılığı beni heyecanlandırıyor. Uyum sağlayabiliyorum ve bunu rolün en heyecan verici yönlerinden biri olarak görüyorum."
      },
      {
        question: "How would you handle being away from family for extended periods?",
        question_tr: "Uzun süreler ailenizden uzak kalmayı nasıl karşılarsınız?",
        tips: ["Show preparation", "Discuss support system", "Focus on positive aspects"],
        sample_answer: "I've discussed this with my family and have their full support. We're prepared for the distance and have planned regular video calls and visits. I see it as an opportunity for both personal and professional growth.",
        sample_answer_tr: "Bunu ailemle konuştum ve tam desteklerini aldım. Uzaklığa hazırız ve düzenli video görüşmeleri ve ziyaretler planladık. Bunu hem kişisel hem de profesyonel büyüme için bir fırsat olarak görüyorum."
      },
      {
        question: "What do you think makes our airline different from competitors?",
        question_tr: "Havayolumuzun rakiplerden farkı ne sizce?",
        tips: ["Research thoroughly", "Be specific", "Relate to your values"],
        sample_answer: "Your airline stands out for its investment in employee training and customer experience. The emphasis on cultural diversity and the extensive network truly sets you apart in the industry.",
        sample_answer_tr: "Havayolunuz çalışan eğitimine ve müşteri deneyimine yaptığı yatırımla öne çıkıyor. Kültürel çeşitliliğe verilen önem ve geniş ağ, sizi sektörde gerçekten farklı kılıyor."
      },
      {
        question: "How do you feel about wearing a uniform?",
        question_tr: "Üniforma giymek hakkında ne düşünüyorsunuz?",
        tips: ["Positive attitude", "Pride in representation", "Professional image"],
        sample_answer: "I'm proud to wear a uniform as it represents the professionalism and values of the company. It creates unity among the crew and helps passengers identify us easily.",
        sample_answer_tr: "Üniforma giymekten gurur duyuyorum çünkü şirketin profesyonelliğini ve değerlerini temsil ediyor. Ekip arasında birlik yaratır ve yolcuların bizi kolayca tanımasına yardımcı olur."
      },
      {
        question: "Can you work irregular hours including nights and holidays?",
        question_tr: "Gece ve tatiller dahil düzensiz saatlerde çalışabilir misiniz?",
        tips: ["Show commitment", "Acknowledge challenges", "Express enthusiasm"],
        sample_answer: "Yes, I understand and accept that this is part of the job. I actually enjoy the variety that comes with irregular schedules, and I'm prepared for the lifestyle that comes with being cabin crew.",
        sample_answer_tr: "Evet, bunun işin bir parçası olduğunu anlıyor ve kabul ediyorum. Düzensiz programlarla gelen çeşitlilikten aslında keyif alıyorum ve kabin memuru olmanın getirdiği yaşam tarzına hazırım."
      }
    ]
  }
];

export default function CabinCrewInterview() {
  const { user } = useContext(AuthContext);
  const navigate = useNavigate();
  
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [currentQuestionIndex, setCurrentQuestionIndex] = useState(0);
  const [showAnswer, setShowAnswer] = useState(false);
  const [isRecording, setIsRecording] = useState(false);
  const [practiceMode, setPracticeMode] = useState('view'); // 'view' or 'practice'
  const [completedQuestions, setCompletedQuestions] = useState([]);
  const [score, setScore] = useState(0);
  const [audioPlaying, setAudioPlaying] = useState(false);
  
  const audioRef = useRef(null);

  // Demo kullanıcıları bu sayfaya erişemez
  useEffect(() => {
    if (user?.is_demo) {
      toast.error('Bu özellik tam sürümde mevcuttur');
      navigate('/dashboard');
    }
  }, [user, navigate]);

  const currentCategory = INTERVIEW_CATEGORIES.find(c => c.id === selectedCategory);
  const currentQuestion = currentCategory?.questions[currentQuestionIndex];

  const playAudio = async (text) => {
    if (!text) return;
    
    // First try browser TTS as primary (OpenAI rate limited)
    if ('speechSynthesis' in window) {
      try {
        setAudioPlaying(true);
        const utterance = new SpeechSynthesisUtterance(text);
        utterance.lang = 'en-US';
        utterance.rate = 0.9;
        utterance.onend = () => setAudioPlaying(false);
        utterance.onerror = () => setAudioPlaying(false);
        speechSynthesis.speak(utterance);
        return;
      } catch (e) {
        console.error('Browser TTS failed:', e);
      }
    }
    
    // Fallback to API if browser TTS not available
    try {
      setAudioPlaying(true);
      const response = await axios.post(
        `${API_URL}/api/speaking/text-to-speech`,
        { text },
        { responseType: 'blob' }
      );
      const contentType = response.headers['content-type'] || '';
      if (contentType.includes('audio')) {
        const audioUrl = URL.createObjectURL(response.data);
        if (audioRef.current) {
          audioRef.current.src = audioUrl;
          audioRef.current.play();
        }
      }
    } catch (error) {
      console.error('Audio API error:', error);
      toast.error('Ses çalınamadı');
    } finally {
      setAudioPlaying(false);
    }
  };

  const handleNextQuestion = () => {
    if (!completedQuestions.includes(currentQuestionIndex)) {
      setCompletedQuestions([...completedQuestions, currentQuestionIndex]);
      setScore(score + 10);
    }
    
    if (currentQuestionIndex < currentCategory.questions.length - 1) {
      setCurrentQuestionIndex(currentQuestionIndex + 1);
      setShowAnswer(false);
    } else {
      // Category completed
      toast.success('Kategori tamamlandı! 🎉');
    }
  };

  const resetCategory = () => {
    setCurrentQuestionIndex(0);
    setShowAnswer(false);
    setCompletedQuestions([]);
    setScore(0);
  };

  // Category selection screen
  if (!selectedCategory) {
    return (
      <CabinCrewLayout>
        <div className="min-h-screen bg-gradient-to-br from-indigo-50 via-purple-50 to-pink-50 p-4">
          <audio ref={audioRef} onEnded={() => setAudioPlaying(false)} />
          
          <div className="container mx-auto max-w-4xl">
            {/* Header */}
            <div className="flex items-center gap-4 mb-8">
              <Button
                variant="ghost"
                onClick={() => navigate('/dashboard')}
              >
                <ChevronLeft className="w-5 h-5 mr-1" />
                Ana Sayfa
              </Button>
              <div>
                <h1 className="text-3xl font-black text-gray-800 flex items-center gap-3">
                  <Users className="w-8 h-8 text-indigo-500" />
                  Mülakat Simülasyonu
                </h1>
                <p className="text-gray-600">Gerçek mülakat deneyimi ile hazırlan</p>
              </div>
            </div>

          {/* Introduction Card */}
          <Card className="mb-8 border-2 border-indigo-200 bg-gradient-to-r from-indigo-50 to-purple-50">
            <CardContent className="p-6">
              <div className="flex items-start gap-4">
                <div className="w-16 h-16 bg-gradient-to-br from-indigo-500 to-purple-600 rounded-xl flex items-center justify-center flex-shrink-0">
                  <Sparkles className="w-8 h-8 text-white" />
                </div>
                <div>
                  <h3 className="text-xl font-bold text-gray-800 mb-2">Mülakat Hazırlığı</h3>
                  <p className="text-gray-600 mb-4">
                    Kabin memurluğu mülakatlarında sıkça sorulan soruları pratik edin. 
                    Her kategori farklı soru tiplerini içerir. Sesli pratik yapabilir, 
                    örnek cevapları dinleyebilirsiniz.
                  </p>
                  <div className="flex gap-2 flex-wrap">
                    <Badge className="bg-blue-500 text-white">Kişisel Sorular</Badge>
                    <Badge className="bg-purple-500 text-white">Senaryo Soruları</Badge>
                    <Badge className="bg-green-500 text-white">Yetkinlik Soruları</Badge>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Category Selection */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {INTERVIEW_CATEGORIES.map((category) => (
              <Card 
                key={category.id}
                className="cursor-pointer hover:shadow-xl transition-all hover:-translate-y-2 border-2 border-transparent hover:border-indigo-300"
                onClick={() => setSelectedCategory(category.id)}
              >
                <CardContent className="p-6 text-center">
                  <div className={`w-20 h-20 bg-gradient-to-br ${category.color} rounded-2xl flex items-center justify-center mx-auto mb-4`}>
                    <span className="text-4xl">{category.icon}</span>
                  </div>
                  <h3 className="text-xl font-bold text-gray-800 mb-2">{category.name}</h3>
                  <p className="text-sm text-gray-500 mb-4">{category.description}</p>
                  <Badge className={`bg-gradient-to-r ${category.color} text-white`}>
                    {category.questions.length} Soru
                  </Badge>
                </CardContent>
              </Card>
            ))}
          </div>

          {/* Tips */}
          <Card className="mt-8 bg-amber-50 border-amber-200">
            <CardContent className="p-6">
              <h3 className="font-bold text-amber-800 mb-3 flex items-center gap-2">
                <MessageSquare className="w-5 h-5" />
                Mülakat İpuçları
              </h3>
              <ul className="text-sm text-amber-700 space-y-2">
                <li>• <strong>STAR Yöntemi:</strong> Durum, Görev, Eylem, Sonuç şeklinde cevap verin</li>
                <li>• <strong>Göz teması:</strong> Güven verici ve profesyonel görünün</li>
                <li>• <strong>Beden dili:</strong> Dik oturun, gülümseyin, el hareketlerinize dikkat edin</li>
                <li>• <strong>Hazırlık:</strong> Şirket hakkında araştırma yapın</li>
              </ul>
            </CardContent>
          </Card>
        </div>
      </div>
      </CabinCrewLayout>
    );
  }

  // Interview practice screen
  return (
    <CabinCrewLayout>
      <div className="min-h-screen bg-gradient-to-br from-indigo-50 via-purple-50 to-pink-50 p-4">
        <audio ref={audioRef} onEnded={() => setAudioPlaying(false)} />
        
        <div className="container mx-auto max-w-2xl">
          {/* Header */}
          <div className="flex items-center justify-between mb-6">
          <Button
            variant="ghost"
            onClick={() => setSelectedCategory(null)}
          >
            <ChevronLeft className="w-5 h-5" />
            Kategoriler
          </Button>
          <div className="text-center">
            <p className="text-sm text-gray-500">{currentCategory?.name}</p>
            <p className="font-bold text-gray-800">
              Soru {currentQuestionIndex + 1} / {currentCategory?.questions.length}
            </p>
          </div>
          <Badge className="bg-indigo-500 text-white">
            <Star className="w-3 h-3 mr-1" />
            {score} Mil
          </Badge>
        </div>

        {/* Progress */}
        <Progress 
          value={((currentQuestionIndex + 1) / currentCategory?.questions.length) * 100}
          className="h-2 mb-6"
        />

        {/* Question Card */}
        <Card className="border-2 border-indigo-200 shadow-xl mb-6">
          <CardContent className="p-6">
            {/* Interviewer avatar */}
            <div className="flex items-center gap-3 mb-6">
              <div className="w-12 h-12 bg-gradient-to-br from-indigo-500 to-purple-600 rounded-full flex items-center justify-center">
                <Users className="w-6 h-6 text-white" />
              </div>
              <div>
                <p className="font-bold text-gray-800">Mülakatçı</p>
                <p className="text-sm text-gray-500">HR Manager</p>
              </div>
              <button
                onClick={() => playAudio(currentQuestion?.question)}
                disabled={audioPlaying}
                className="ml-auto w-10 h-10 bg-indigo-100 rounded-full flex items-center justify-center hover:bg-indigo-200 transition-colors"
              >
                {audioPlaying ? (
                  <Loader2 className="w-5 h-5 text-indigo-600 animate-spin" />
                ) : (
                  <Volume2 className="w-5 h-5 text-indigo-600" />
                )}
              </button>
            </div>

            {/* Question */}
            <div className="bg-indigo-50 rounded-xl p-4 mb-4">
              <p className="text-lg font-medium text-gray-800 mb-2">
                {currentQuestion?.question}
              </p>
              <p className="text-sm text-gray-600 italic">
                {currentQuestion?.question_tr}
              </p>
            </div>

            {/* Tips */}
            {currentQuestion?.tips && (
              <div className="bg-amber-50 rounded-xl p-4 mb-4">
                <p className="text-sm font-bold text-amber-700 mb-2">💡 Cevap İpuçları:</p>
                <ul className="text-sm text-gray-600 space-y-1">
                  {currentQuestion.tips.map((tip, idx) => (
                    <li key={idx}>• {tip}</li>
                  ))}
                </ul>
              </div>
            )}

            {/* Voice Practice Button */}
            <div className="flex justify-center mb-6">
              <button
                onClick={() => setIsRecording(!isRecording)}
                className={`w-20 h-20 rounded-full flex items-center justify-center transition-all ${
                  isRecording 
                    ? 'bg-red-500 hover:bg-red-600 animate-pulse' 
                    : 'bg-gradient-to-br from-pink-500 to-purple-600 hover:scale-110'
                }`}
              >
                {isRecording ? (
                  <MicOff className="w-10 h-10 text-white" />
                ) : (
                  <Mic className="w-10 h-10 text-white" />
                )}
              </button>
            </div>
            <p className="text-center text-sm text-gray-500 mb-4">
              {isRecording ? '🔴 Kayıt yapılıyor...' : 'Sesli cevap vermek için tıklayın'}
            </p>

            {/* Show/Hide Answer */}
            <Button
              variant="outline"
              className="w-full mb-4"
              onClick={() => setShowAnswer(!showAnswer)}
            >
              {showAnswer ? 'Cevabı Gizle' : 'Örnek Cevabı Göster'}
            </Button>

            {/* Sample Answer */}
            {showAnswer && (
              <div className="bg-green-50 rounded-xl p-4 border-2 border-green-200">
                <div className="flex items-center justify-between mb-2">
                  <p className="text-sm font-bold text-green-700">✨ Örnek Cevap:</p>
                  <button
                    onClick={() => playAudio(currentQuestion?.sample_answer)}
                    disabled={audioPlaying}
                    className="w-8 h-8 bg-green-100 rounded-full flex items-center justify-center hover:bg-green-200"
                  >
                    <Volume2 className="w-4 h-4 text-green-600" />
                  </button>
                </div>
                <p className="text-gray-700 mb-2">{currentQuestion?.sample_answer}</p>
                <p className="text-sm text-gray-500 italic">{currentQuestion?.sample_answer_tr}</p>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Navigation */}
        <div className="flex gap-3">
          <Button
            variant="outline"
            onClick={() => {
              if (currentQuestionIndex > 0) {
                setCurrentQuestionIndex(currentQuestionIndex - 1);
                setShowAnswer(false);
              }
            }}
            disabled={currentQuestionIndex === 0}
            className="flex-1"
          >
            <ChevronLeft className="w-4 h-4 mr-1" />
            Önceki
          </Button>
          
          <Button
            onClick={handleNextQuestion}
            className="flex-1 bg-gradient-to-r from-indigo-500 to-purple-600"
          >
            {currentQuestionIndex === currentCategory.questions.length - 1 ? (
              <>
                Tamamla
                <CheckCircle className="w-4 h-4 ml-1" />
              </>
            ) : (
              <>
                Sonraki
                <ChevronRight className="w-4 h-4 ml-1" />
              </>
            )}
          </Button>
        </div>

        {/* Completion indicator */}
        <div className="mt-6 flex justify-center gap-2">
          {currentCategory?.questions.map((_, idx) => (
            <div
              key={idx}
              className={`w-3 h-3 rounded-full ${
                completedQuestions.includes(idx)
                  ? 'bg-green-500'
                  : idx === currentQuestionIndex
                  ? 'bg-indigo-500'
                  : 'bg-gray-300'
              }`}
            />
          ))}
        </div>
      </div>
    </div>
    </CabinCrewLayout>
  );
}
